import { useAsyncError } from '@/lib/hooks/useAsyncError';
import { useCurrentFlow } from '@/lib/hooks/useCurrentFlow';
import { useCurrentUser } from '@/lib/hooks/useCurrentUser';
import { useLatestStep } from '@/lib/hooks/useLatestStep';
import { RecWizardContext, type RecWizardContextProps } from '@/lib/hooks/useRecWizard';
import { fetchSessionWizard, syncRecWizard, updateSessionWizard } from '@/lib/utils/recWizard';
import { type RecWizard } from '@/types/recWizard';
import { type Recommendations } from '@/types/recommendations';
import { type Step } from '@/types/steps';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const RecWizardProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { pathname, search } = useLocation();
  const { currentUser, loading: loadingUser } = useCurrentUser();
  const navigate = useNavigate();

  const [wizard, setWizard] = useState<Partial<RecWizard>>();
  const [recommendations, setRecommendations] = useState<Recommendations>();
  const [loading, setLoading] = useState(true);

  const throwAsyncError = useAsyncError();
  const latestStep = useLatestStep(wizard);
  const { flow } = useCurrentFlow();

  // Sync wizard changes when requested
  const [needsSync, setNeedsSync] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const syncWizard = () => setNeedsSync(true);

  const updateWizard: RecWizardContextProps['updateWizard'] = useCallback(
    (values) => setWizard((prevWizard) => ({ ...prevWizard, ...values, latestStep })),
    [latestStep]
  );

  const updateRecommendations = useCallback((data?: Recommendations) => {
    setRecommendations({
      planRecommendations: data?.planRecommendations,
      recommendedRecipes: data?.recommendedRecipes || [],
      recommendedComboRecipes: data?.recommendedComboRecipes || [],
      recommendedAddons: data?.recommendedAddons || [],
      recommendedRecipeLineItems: data?.recommendedRecipeLineItems || []
    });
  }, []);

  const startWizard = () => {
    fetchSessionWizard({ flow, step: pathname as Step['fullPath'], currentUser })
      .then((wizardOrRedirect) => {
        if (typeof wizardOrRedirect === 'string') {
          // Reset loading flag to reload the cookie data after redirect
          setLoading(true);
          // Keep the query params when redirecting
          navigate({ pathname: wizardOrRedirect, search });
        } else {
          setLoading(false);
          updateWizard(wizardOrRedirect);
        }
        if (!recommendations) syncWizard();
      })
      .catch(throwAsyncError);
  };

  useEffect(() => {
    if (wizard) updateSessionWizard(wizard);
  }, [wizard]);

  // Fetch wizard from cookie or init a new one
  useEffect(() => {
    if (!wizard && !loadingUser) startWizard();
  }, [pathname, loadingUser]);

  useEffect(() => {
    if (needsSync && wizard?.id) {
      setSyncing(true);
      syncRecWizard(wizard)
        .then((resp) => {
          const wizardData = resp.recommendationWizardUpdate?.recommendationWizard;
          updateRecommendations(wizardData);
          setNeedsSync(false);
        })
        .finally(() => setSyncing(false));
    }
  }, [needsSync, wizard]);

  return (
    <RecWizardContext.Provider
      value={{
        wizard,
        recommendations,
        updateWizard,
        loading,
        syncing: syncing || needsSync,
        isSynced: !needsSync && !syncing,
        syncWizard
      }}
    >
      {children}
    </RecWizardContext.Provider>
  );
};
